import type { SandboxProviderCreds } from "@/frontend/types/sandbox-credentials";

const AETNA_PASSWORD = "FHIRdemo2020";
const CERNER_PASSWORD = "Cerner01";
const EPIC_MYCHART_PASSWORD = "epicepic1";
const ATHENA_PASSWORD = "Password1";
const ECW_PASSWORD = "e@CWFHIR1";

/** Aetna Patient Access API sandbox test users, from `sandbox_v2_test_member_logins_and_test_data_v2.1.xlsx` (sheet: "Pat Access & P2P Test Data"). VTETestUser20 is Aetna's canned commercial-member error scenario and is deliberately omitted. */
const AETNA_SANDBOX: SandboxProviderCreds = {
  providerType: "aetna",
  matchFhirBaseUrl: (url) => /vteapi[a-z0-9]+\.aetna\.com/i.test(url) || /sandbox\.aetna\.com/i.test(url) || /aetna\.com\/fhirdemo/i.test(url),
  displayName: "Aetna Sandbox",
  helperText: `Every account uses the same password (${AETNA_PASSWORD}). Copy a username + password, click Continue, then sign in at Aetna.`,
  rows: Array.from({ length: 19 }, (_, i) => ({
    username: `VTETestUser${String(i + 1).padStart(2, "0")}`,
    password: AETNA_PASSWORD,
  })),
};

/** Cerner (Oracle Health) patient sandbox test personas. Matches by the well-known public sandbox tenant UUID rather than hostname. The `fhir-ehr-code` host is Cerner's provider code sandbox (practitioner NPIs, different personas); the Nancy/Joe personas live on the patient side (`fhir-myrecord` or `fhir-open`) under this tenant. Any other tenant UUID is a real customer environment. */
const CERNER_SANDBOX: SandboxProviderCreds = {
  providerType: "cerner",
  matchFhirBaseUrl: (url) => /ec2458f2-1e24-41c8-b71b-0e701af7583d/i.test(url) && !/fhir-ehr-code\.cerner\.com/i.test(url),
  displayName: "Cerner Sandbox",
  helperText: `Every persona uses the same password (${CERNER_PASSWORD}). Copy a username + password, click Continue, then sign in at Cerner.`,
  rows: [
    { username: "nancysmart", password: CERNER_PASSWORD },
    { username: "joesmart", password: CERNER_PASSWORD },
    { username: "wilmasmart", password: CERNER_PASSWORD },
    { username: "valeriesmart", password: CERNER_PASSWORD },
    { username: "fredricksmart", password: CERNER_PASSWORD },
    { username: "sandysmart", password: CERNER_PASSWORD },
    { username: "timmysmart", password: CERNER_PASSWORD },
    { username: "babyboysmart", password: CERNER_PASSWORD },
    { username: "haileysmart", password: CERNER_PASSWORD },
    { username: "timpeters", password: CERNER_PASSWORD },
  ],
};

/** Epic on FHIR sandbox. Lists the three MyChart-enabled test patients from `fhir.epic.com/Documentation?docId=testpatients`. Other listed patients on the doc page are Epic-only records without MyChart logins, so they cannot complete the patient-facing OAuth flow and are omitted. */
const EPIC_SANDBOX: SandboxProviderCreds = {
  providerType: "epic",
  matchFhirBaseUrl: (url) => /fhir\.epic\.com\/interconnect-fhir-oauth/i.test(url),
  displayName: "Epic Sandbox",
  helperText: `Every MyChart test patient uses the same password (${EPIC_MYCHART_PASSWORD}). Copy a username + password, click Continue, then sign in at Epic.`,
  rows: [
    { username: "fhircamila", password: EPIC_MYCHART_PASSWORD },
    { username: "fhirderrick", password: EPIC_MYCHART_PASSWORD },
    { username: "fhirdesiree", password: EPIC_MYCHART_PASSWORD },
  ],
};

/** athenahealth PHR test patient. Only one canonical login on the athenahealth preview environment. */
const ATHENA_SANDBOX: SandboxProviderCreds = {
  providerType: "athena",
  matchFhirBaseUrl: (url) => /api\.preview\.platform\.athenahealth\.com/i.test(url),
  displayName: "athenahealth Sandbox",
  helperText: "Copy the username + password, click Continue, then sign in at athenahealth.",
  rows: [
    { username: "phrtest_preview@mailinator.com", password: ATHENA_PASSWORD },
  ],
};

/** eClinicalWorks / healow FHIR R4 sandbox. One adult test patient exposed on the healow developer tenant. */
const ECW_SANDBOX: SandboxProviderCreds = {
  providerType: "eclinicalworks",
  matchFhirBaseUrl: (url) => /fhir4\.healow\.com/i.test(url),
  displayName: "eClinicalWorks Sandbox",
  helperText: "Copy the username + password, click Continue, then sign in at healow.",
  rows: [
    { username: "AdultFemaleFHIR", password: ECW_PASSWORD },
  ],
};

/** CMS Blue Button 2.0 sandbox synthetic beneficiaries. BB2 accepts any 5-digit index (`BBUser00042` / `PW00042!` works too). Listing five samples keeps the dialog compact while covering the pattern. */
const MEDICARE_SANDBOX: SandboxProviderCreds = {
  providerType: "medicare",
  matchFhirBaseUrl: (url) => /sandbox\.bluebutton\.cms\.gov/i.test(url),
  displayName: "CMS Blue Button Sandbox",
  helperText: "Any 5-digit index works. The username is BBUserNNNNN and the password is PWNNNNN! with the same NNNNN. Five samples below.",
  rows: [
    { username: "BBUser00000", password: "PW00000!" },
    { username: "BBUser00001", password: "PW00001!" },
    { username: "BBUser00002", password: "PW00002!" },
    { username: "BBUser00003", password: "PW00003!" },
    { username: "BBUser00004", password: "PW00004!" },
  ],
};

export const SANDBOX_CREDENTIALS: SandboxProviderCreds[] = [
  AETNA_SANDBOX,
  CERNER_SANDBOX,
  EPIC_SANDBOX,
  ATHENA_SANDBOX,
  ECW_SANDBOX,
  MEDICARE_SANDBOX,
];
